import { mdiArrowDown } from '@mdi/js';
import { Button } from '@mollybet/ui';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { StyledButton, DangerButton } from 'common/components/buttons';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import {
  FilterProp,
  FilterTypes,
  useFiltration,
  OptionsFilterComponentHooks,
  GLEFilterComponentHooks,
  FiltersContainer,
} from 'common/components/table/filtering';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  ClientIncomingTransfersActions,
  ClientTransfer,
  ClientTransferStatuses,
} from 'common/state/banking/transfers/actions';
import { incomingClientTransfersRecipe } from 'common/state/banking/transfers/recipes';
import {
  ClientTransfersState,
  defaultClientTransfersState,
} from 'common/state/banking/transfers/states';
import { doGetClientIncomingTransfers } from 'common/state/banking/transfers/thunkActions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalMode } from '../agentBanking';

interface DepositsTableProps {
  setSelectedRow: (transferRequest: ClientTransfer) => void;
  setModalMode: (mode: ModalMode) => void;
}

const STATUS_OPTIONS = [
  { value: undefined, label: 'All' },
  { value: ClientTransferStatuses.SENT, label: 'Sent' },
  { value: ClientTransferStatuses.ACCEPTED, label: 'Accepted' },
  { value: ClientTransferStatuses.UNACCEPTED, label: 'Unaccepted' },
];

const DateInputsContainer = styled.div`
  .eq {
    display: none;
  }
`;

export const DepositsTable = ({
  setSelectedRow,
  setModalMode,
}: DepositsTableProps) => {
  const [state, dispatch] = useProducerWithThunks<
    ClientTransfersState,
    ClientIncomingTransfersActions
  >(incomingClientTransfersRecipe, defaultClientTransfersState);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [destinations, setDestinations] = useState<{
    [url: string]: Record<string, string>;
  }>({});
  const dropdownRef = useRef();

  const columns = useMemo(
    (): ColumnProp<ClientTransfer>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.date).toLocaleDateString(),
      },
      {
        key: 'bank_date',
        title: 'Bank Date',
        valueFormatter: (row) => row.bank_date ? new Date(row.bank_date).toLocaleDateString(): '',
      },
      {
        key: 'sender',
        title: 'Sender',
        valueFormatter: (row) => row.sender.split('/').slice(-2, -1)[0],
      },
      { key: 'description', title: 'Description' },
      {
        key: 'destination',
        title: 'Destination',
        valueFormatter: (row) =>
          destinations[row.destination]?.title || 'Loading...',
      },
      { key: 'reference', title: 'Reference' },
      { key: 'bank_reference', title: 'Bank Reference' },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement({ colour: false }),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
      {
        key: 'got_amount',
        title: 'Got Amount',
        valueFormatter: (row) =>
          new Currency(row.got_amount, row.got_currency).toElement({
            colour: false,
          }),
        csvFormatter: (row) =>
          new Currency(row.got_amount, row.got_currency).toString({
            symbol: false,
          }),
      },
      { key: 'status', title: 'Status' },
      {
        key: 'action',
        title: 'Actions',
        valueFormatter: (row) => (
          <div style={{ display: 'flex' }}>
            <StyledButton
              disabled={row.status !== ClientTransferStatuses.SENT}
              onClick={() => {
                setSelectedRow(row);
                setModalMode('depositAccept');
              }}
            >
              Accept
            </StyledButton>
            <DangerButton
              disabled={row.status !== ClientTransferStatuses.SENT}
              onClick={() => {
                setSelectedRow(row);
                setModalMode('depositDecline');
              }}
            >
              Decline
            </DangerButton>
          </div>
        ),
      },
    ],
    [destinations, setSelectedRow, setModalMode]
  );

  const filters = useMemo(
    (): FilterProp<ClientTransfer>[] => [
      {
        key: 'status',
        label: 'Status',
        type: FilterTypes.OPTIONS,
        options: STATUS_OPTIONS,
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
      {
        key: 'date',
        label: 'Date',
        type: FilterTypes.DATE,
      },
    ],
    // eslint-disable-next-line
    [dropdownRef.current]
  );

  const [rows, filterComponents, filterHooks] = useFiltration(
    state.transfers || [],
    filters
  );

  filterHooks.status =
    filterHooks?.status as OptionsFilterComponentHooks<ClientTransfer>;
  filterHooks.date =
    filterHooks?.date as GLEFilterComponentHooks<ClientTransfer>;
  filterHooks.date =
    filterHooks?.date as GLEFilterComponentHooks<ClientTransfer>;

  useEffect(() => {
    const filters = {
      limit: pageSize,
      offset: pageSize * (pageNo - 1),
    };

    filterHooks.status =
      filterHooks?.status as OptionsFilterComponentHooks<ClientTransfer>;
    filterHooks.date =
      filterHooks?.date as GLEFilterComponentHooks<ClientTransfer>;
    filterHooks.date =
      filterHooks?.date as GLEFilterComponentHooks<ClientTransfer>;

    if (filterHooks?.status?.value?.value)
      filters['status'] = filterHooks.status.value.value;
    if (filterHooks?.date?.value?.gt)
      filters['start_date'] = filterHooks.date.value.gt;
    if (filterHooks?.date?.value?.lt)
      filters['end_date'] = filterHooks.date.value.lt;

    dispatch(doGetClientIncomingTransfers(filters));
  }, [dispatch, filterHooks, pageSize, pageNo]);

  useEffect(() => {
    if (state.transfers) {
      const urls = state.transfers.reduce((acc, transfer) => {
        if (!(transfer.destination in destinations)) {
          acc.add(transfer.destination);
        }
        return acc;
      }, new Set<string>());

      if (urls?.size) {
        Promise.all(
          Array.from(urls).map((url) =>
            api.get(url).then((response) => response.data)
          )
        ).then((newDestinations) => {
          setDestinations({
            ...destinations,
            ...newDestinations.reduce(
              (acc, destination) => ({
                ...acc,
                [destination.url]: destination,
              }),
              {}
            ),
          });
        });
      }
    }
    //eslint-disable-next-line
  }, [state.transfers]);

  return (
    <>
      <TableTitle icon={mdiArrowDown} title="Deposits" />
      <Button
        variant="primary"
        onClick={() => setModalMode('depositNew')}
        style={{ marginLeft: '1rem' }}
      >
        Create New
      </Button>
      <FiltersContainer hooks={filterHooks}>
        {filterComponents.status}
        <br />
        <DateInputsContainer>{filterComponents.date}</DateInputsContainer>
      </FiltersContainer>
      <div ref={dropdownRef} />
      <br />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="Deposits"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
